import React, { useEffect, useState } from "react";
import { Editor } from "@tinymce/tinymce-react";
import DialogBox from "@/components/Common/DialogBox";
import Dropdown from "@/components/ui/Dropdown";
import useToastStore from "@/components/Common/Toast/toastStore";
import { marketingCampaignService } from "@/services/marketingCampaignService";

const TINYMCE_INIT = {
  height: 280,
  menubar: false,
  plugins: [
    "advlist",
    "autolink",
    "lists",
    "link",
    "charmap",
    "preview",
    "anchor",
    "searchreplace",
    "visualblocks",
    "code",
    "help",
    "wordcount",
  ],
  toolbar:
    "undo redo | blocks | bold italic forecolor | alignleft aligncenter alignright | bullist numlist | link | removeformat | help",
  content_style:
    "body { font-family: -apple-system, BlinkMacSystemFont, San Francisco, Segoe UI, Roboto, Helvetica Neue, sans-serif; font-size: 14px }",
  skin: "oxide",
  content_css: "default",
  branding: false,
  statusbar: true,
  resize: true,
  promotion: false,
};

const CHANNEL_OPTIONS = [
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
  { value: "both", label: "Email & SMS" },
];

const emptyForm = {
  name: "",
  channel: "email",
  subject: "",
  email_body: "",
  sms_body: "",
};

const isEmptyHtml = (value = "") =>
  !value.trim() || value.trim() === "<p><br></p>";

const MarketingCampaignModal = ({
  isOpen,
  onClose,
  campaign = null,
  onSaved,
}) => {
  const [form, setForm] = useState(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const { showToast } = useToastStore;

  const isEditing = Boolean(campaign?.id);
  const includesEmail = form.channel === "email" || form.channel === "both";
  const includesSms = form.channel === "sms" || form.channel === "both";

  useEffect(() => {
    if (!isOpen) {
      setForm(emptyForm);
      setIsSaving(false);
      return;
    }

    if (campaign) {
      setForm({
        name: campaign.name || "",
        channel: campaign.channel || "email",
        subject: campaign.subject || "",
        email_body: campaign.email_body || campaign.body || "",
        sms_body: campaign.sms_body || "",
      });
    } else {
      setForm(emptyForm);
    }
  }, [isOpen, campaign]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!form.name.trim()) {
      showToast({ message: "Please enter a template name", type: "error" });
      return false;
    }

    if (!form.channel) {
      showToast({ message: "Please select a channel", type: "error" });
      return false;
    }

    if (includesEmail && !form.subject.trim()) {
      showToast({ message: "Please enter an email subject", type: "error" });
      return false;
    }

    if (includesEmail && isEmptyHtml(form.email_body)) {
      showToast({ message: "Please enter email content", type: "error" });
      return false;
    }

    if (includesSms && isEmptyHtml(form.sms_body)) {
      showToast({ message: "Please enter an SMS message", type: "error" });
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsSaving(true);

    try {
      const payload = {
        name: form.name.trim(),
        channel: form.channel,
        subject: includesEmail ? form.subject.trim() : form.name.trim(),
        body: includesEmail ? form.email_body.trim() : "",
        sms_body: includesSms ? form.sms_body.trim() : "",
      };

      if (isEditing) {
        await marketingCampaignService.updateCampaign(campaign.id, payload);
        showToast({ message: "Template updated successfully", type: "success" });
      } else {
        await marketingCampaignService.createCampaign(payload);
        showToast({ message: "Template created successfully", type: "success" });
      }

      await onSaved?.();
      onClose();
    } catch (error) {
      showToast({
        message: error.message || "Failed to save template",
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <DialogBox
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? "Edit Template" : "Add Template"}
      className="w-[55%] max-md:w-[90%]"
      showIcon={false}
      actionButton={
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className={`bg-primary-dark text-white px-5 py-2 rounded-lg hover:bg-primary-dark transition-colors ${isSaving ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          {isSaving
            ? "Saving..."
            : isEditing
              ? "Update Template"
              : "Save Template"}
        </button>
      }
    >
      <div className="space-y-4">
        <div>
          <label
            htmlFor="campaign-name"
            className="block text-sm font-medium text-foreground mb-1"
          >
            Template Name
          </label>
          <input
            id="campaign-name"
            type="text"
            value={form.name}
            onChange={(e) => updateField("name", e.target.value)}
            className="border border-input bg-background text-foreground placeholder:text-muted-foreground rounded-lg p-2 w-full outline-none focus:ring-1 focus:ring-ring"
            placeholder="e.g. Summer hiring update"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Channel
          </label>
          <Dropdown
            className="w-full"
            options={CHANNEL_OPTIONS}
            value={
              CHANNEL_OPTIONS.find((option) => option.value === form.channel) ||
              CHANNEL_OPTIONS[0]
            }
            placeholder="Select channel"
            onChange={(selectedOption) =>
              updateField("channel", selectedOption?.value || "email")
            }
            triggerClassName="!bg-background !border !border-input hover:!border-primary"
          />
        </div>

        {includesEmail && (
          <>
            <div>
              <label
                htmlFor="campaign-subject"
                className="block text-sm font-medium text-foreground mb-1"
              >
                Email Subject
              </label>
              <input
                id="campaign-subject"
                type="text"
                value={form.subject}
                onChange={(e) => updateField("subject", e.target.value)}
                className="border border-input bg-background text-foreground placeholder:text-muted-foreground rounded-lg p-2 w-full outline-none focus:ring-1 focus:ring-ring"
                placeholder="Enter email subject..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Email Content
              </label>
              <Editor
                key={`email-editor-${form.channel}-${campaign?.id || "new"}`}
                apiKey={import.meta.env.VITE_TINYMCE_API_KEY}
                value={form.email_body}
                onEditorChange={(value) => updateField("email_body", value)}
                init={{
                  ...TINYMCE_INIT,
                  placeholder: "Write your email content...",
                }}
              />
            </div>
          </>
        )}

        {includesSms && (
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              SMS Message
            </label>
            <Editor
              key={`sms-editor-${form.channel}-${campaign?.id || "new"}`}
              apiKey={import.meta.env.VITE_TINYMCE_API_KEY}
              value={form.sms_body}
              onEditorChange={(value) => updateField("sms_body", value)}
              init={{
                ...TINYMCE_INIT,
                height: 200,
                placeholder: "Write your SMS message...",
              }}
            />
          </div>
        )}
      </div>
    </DialogBox>
  );
};

export default MarketingCampaignModal;
